
import React, { useState } from "react";
import { BarChart3, ChevronLeft, ChevronRight, Calendar, Download, Sliders, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format, addMonths, subMonths } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  LineChart,
  ResponsiveContainer, 
  XAxis, 
  YAxis, 
  Line,
  CartesianGrid, 
  Tooltip, 
  Legend,
  AreaChart,
  Area
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useToast } from "@/components/ui/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

interface FluxoCaixaData {
  dia: string;
  receitas: number;
  despesas: number;
}

interface FluxoCaixaSectionProps {
  periodoSelecionado: Date;
  setPeriodoSelecionado: React.Dispatch<React.SetStateAction<Date>>;
  fluxoCaixaData: FluxoCaixaData[];
  generateRandomData: () => void;
}

const FluxoCaixaSection: React.FC<FluxoCaixaSectionProps> = ({ 
  periodoSelecionado, 
  setPeriodoSelecionado,
  fluxoCaixaData,
  generateRandomData
}) => {
  const { toast } = useToast();
  
  // Estado para configurações do gráfico
  const [chartType, setChartType] = useState<"line" | "area">("area");
  const [showDots, setShowDots] = useState(true);
  const [lineThickness, setLineThickness] = useState(2);
  
  // Navegação de períodos
  const proximoPeriodo = () => {
    setPeriodoSelecionado(prev => addMonths(prev, 1));
    generateRandomData();
    toast({
      title: "Período atualizado",
      description: `Dados carregados para ${format(addMonths(periodoSelecionado, 1), "MMMM 'de' yyyy", { locale: pt })}`,
    });
  };

  const periodoAnterior = () => {
    setPeriodoSelecionado(prev => subMonths(prev, 1));
    generateRandomData();
    toast({
      title: "Período atualizado",
      description: `Dados carregados para ${format(subMonths(periodoSelecionado, 1), "MMMM 'de' yyyy", { locale: pt })}`,
    });
  };
  
  // Funções para exportar e resetar
  const exportarGrafico = () => {
    const svgElement = document.querySelector(".recharts-wrapper svg");
    if (!svgElement) return;
    
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const blob = new Blob([svgData], {type: 'image/svg+xml'});
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `fluxo-caixa-${format(periodoSelecionado, "yyyy-MM")}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Gráfico exportado",
      description: "O arquivo SVG foi baixado com sucesso.",
    });
  };
  
  // Formatador de valores monetários
  const formatoMonetario = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  
  // Formatador compacto para eixo Y
  const formatoCompacto = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      notation: 'compact',
      compactDisplay: 'short',
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };
  
  // Calcular o saldo (receitas - despesas) para cada dia
  const dadosComSaldo = fluxoCaixaData.map(item => ({
    ...item,
    saldo: item.receitas - item.despesas
  }));

  return (
    <Card className="border-0 shadow-none">
      <CardHeader className="p-4 pb-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <BarChart3 className="h-4 w-4 text-gray-500" />
            <h2 className="text-base ml-2 text-gray-700">Fluxo de caixa</h2>
          </div>
          <div className="flex items-center gap-2">
            <button 
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setPeriodoSelecionado(prev => subMonths(prev, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-600 flex items-center">
              <Calendar className="h-3 w-3 mr-1" />
              {format(periodoSelecionado, "MMMM 'de' yyyy", { locale: pt })}
            </span>
            <button 
              className="text-gray-400 hover:text-gray-600"
              onClick={() => setPeriodoSelecionado(prev => addMonths(prev, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        {/* Controles do gráfico */}
        <div className="flex justify-between mb-3 items-center">
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 px-2 text-xs" 
              onClick={generateRandomData}
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Atualizar
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                >
                  <Sliders className="h-3 w-3 mr-1" /> Opções
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-48">
                <DropdownMenuLabel className="text-xs">Visualização</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChartType("line")} className="text-xs">
                  Gráfico de linha {chartType === "line" && "✓"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setChartType("area")} className="text-xs">
                  Gráfico de área {chartType === "area" && "✓"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowDots(!showDots)} className="text-xs">
                  {showDots ? "Esconder pontos" : "Mostrar pontos"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="h-6 px-2 text-xs" 
              onClick={exportarGrafico}
            >
              <Download className="h-3 w-3 mr-1" /> Exportar
            </Button>
          </div>
        </div>
        
        <div className="w-full" style={{ height: "380px" }}>
          {fluxoCaixaData.some(d => d.receitas > 0 || d.despesas > 0) ? (
            <ChartContainer
              config={{
                receitas: { label: "Receitas", color: "#9F9EA1" }, 
                despesas: { label: "Despesas", color: "#8E9196" }, 
                saldo: { label: "Saldo", color: "#8A898C" }        
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                {chartType === "line" ? (
                  <LineChart
                    data={dadosComSaldo}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" opacity={0.3} />
                    <XAxis 
                      dataKey="dia" 
                      tick={{ fontSize: 9 }}
                      tickMargin={5}
                      height={20}
                      stroke="#888"
                    />
                    <YAxis 
                      tickFormatter={formatoCompacto}
                      tick={{ fontSize: 9 }}
                      width={50}
                      stroke="#888"
                    />
                    <ChartTooltip 
                      content={
                        <ChartTooltipContent 
                          formatter={(value) => formatoMonetario(value as number)}
                        />
                      }
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      iconSize={7}
                      fontSize={9}
                    />
                    <Line 
                      type="monotone"
                      dataKey="receitas" 
                      stroke="#9F9EA1" 
                      name="Receitas" 
                      strokeWidth={lineThickness}
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                      activeDot={{ r: 4 }}
                    />
                    <Line 
                      type="monotone"
                      dataKey="despesas" 
                      stroke="#8E9196" 
                      name="Despesas" 
                      strokeWidth={lineThickness}
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                      activeDot={{ r: 4 }}
                    />
                    <Line 
                      type="monotone"
                      dataKey="saldo" 
                      stroke="#8A898C" 
                      name="Saldo" 
                      strokeWidth={lineThickness}
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                      activeDot={{ r: 4 }}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                ) : (
                  <AreaChart
                    data={dadosComSaldo}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200" opacity={0.3} />
                    <XAxis 
                      dataKey="dia" 
                      tick={{ fontSize: 9 }}
                      tickMargin={5}
                      height={20}
                      stroke="#888"
                    />
                    <YAxis 
                      tickFormatter={formatoCompacto}
                      tick={{ fontSize: 9 }}
                      width={50}
                      stroke="#888"
                    />
                    <ChartTooltip 
                      content={
                        <ChartTooltipContent 
                          formatter={(value) => formatoMonetario(value as number)}
                        />
                      }
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      iconSize={7}
                      fontSize={9}
                    />
                    <Area 
                      type="monotone"
                      dataKey="receitas" 
                      name="Receitas"
                      stroke="#9F9EA1"
                      fill="#9F9EA120"
                      strokeWidth={lineThickness}
                      activeDot={{ r: 4 }}
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                    />
                    <Area 
                      type="monotone"
                      dataKey="despesas" 
                      name="Despesas"
                      stroke="#8E9196"
                      fill="#8E919620"
                      strokeWidth={lineThickness}
                      activeDot={{ r: 4 }}
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                    />
                    <Line 
                      type="monotone"
                      dataKey="saldo" 
                      name="Saldo"
                      stroke="#8A898C"
                      strokeWidth={lineThickness}
                      strokeDasharray="5 5"
                      dot={showDots ? { r: 3, strokeWidth: 1 } : false}
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-400">
                <BarChart3 className="h-6 w-6 mx-auto mb-1 opacity-30" />
                <p className="text-xs">Nenhum dado disponível para o período selecionado</p>
                <Button 
                  className="mt-2 text-xs py-1 h-auto bg-gray-200 hover:bg-gray-300 text-gray-600"
                  onClick={generateRandomData}
                >
                  Gerar dados
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FluxoCaixaSection;
